import React from 'react';
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Tooltip } from '@mui/material';
import { TaskChipProps } from '../types';
import { getTaskChipStyles } from './TaskChip.styles';
import { useDeleteDivisionMileStone } from '../../../../../../../../reactQuery/division/divisionMileStoneQuery';
import { useSelector } from 'react-redux';
import { getAuth } from '../../../../../../../../redux/auth/authSlice';
import { useSnackbarContext } from '../../../../../../../../components/globalSnackNotify/globalSnackNotifyContext';
import { useDivisionFeatureFlags } from '../../../../../../../../stateContext/featureFlags/FeatureFlagProvider';

export const TaskChip: React.FC<TaskChipProps> = ({
     task,
     isSelected,
     isDragging,
     onToggle,
     onEdit,
     onDelete,
     selectedMilestone,
     numberOfTasks
}) => {
     const [open, setOpen] = React.useState(false);
     const { mutate: deleteTask } = useDeleteDivisionMileStone();
     const { userInfo } = useSelector(getAuth);
     const { showSnackbar } = useSnackbarContext();
     const { divisionSettingsDelete, divisionSettingsRename } = useDivisionFeatureFlags(userInfo?.userDivCode || "");

     const canDeleteTask = (task: any): boolean => {
          return task.isDefault === null;
     };

     const getChipColor = (task: any) => {
          if (isSelected) {
               return "primary";
          }
     };

     const getChipVariant = (task: any) => {
          if (isSelected) {
               return "outlined";
          }
     };

     const handleClick = (e: React.MouseEvent) => {
          // Prevent click during drag
          if (isDragging) {
               e.preventDefault();
               e.stopPropagation();
               return;
          }
          onToggle(task.taskName);
     };

     const handleDoubleClick = (e: React.MouseEvent) => {
          // Prevent double-click during drag
          if (isDragging) {
               e.preventDefault();
               e.stopPropagation();
               return;
          }
          if (divisionSettingsRename) {
               onEdit(task);
          }
     };

     const handleDelete = (e: React.MouseEvent) => {
          if (!canDeleteTask(task) || !divisionSettingsDelete) return;

               deleteTask({
                    id: task.id,
                    divisionCode: task?.divisionCode || userInfo?.userDivCode
               }, {
                    onSuccess: () => {
                         showSnackbar("Task deleted successfully!", "success");
                         setOpen(false);
                         },
                         onError: () => {
                              showSnackbar("Failed to delete task", "error");
                              setOpen(false);
                         },
                    }
               );
     };

     return (
          <>
               <Tooltip title={task.isDefault !== null ? "Cannot edit predefined task" : (divisionSettingsRename ? "Double-click to edit task name" : "")} arrow placement='bottom'>
                    <span>
                         <Chip
                              label={task.taskName}
                              onClick={handleClick}
                              onDoubleClick={divisionSettingsRename && task.isDefault === null ? handleDoubleClick : undefined}
                              onDelete={onDelete && canDeleteTask(task) && divisionSettingsDelete ? (() => setOpen(true)) : undefined}
                              color={getChipColor(task)}
                              variant={getChipVariant(task)}
                              sx={getTaskChipStyles(isDragging, isSelected, task, canDeleteTask)}
                              disabled={task.isDefault !== null}
                         />
                    </span>
               </Tooltip>
               <Dialog open={open} onClose={() => setOpen(false)}>
                    <DialogTitle>Delete Task</DialogTitle>
                    <DialogContent>
                         <DialogContentText>
                              {numberOfTasks !== 1 ? (
                                   <>
                                        Are you sure you want to delete task{' '}
                                        <span style={{ fontWeight: "bold", wordBreak: "break-word", whiteSpace: "normal", display: "inline" }}>
                                             {task.taskName}
                                        </span>
                                        ? This action cannot be undone.
                                   </>
                              ) : (
                                   "You cannot delete the only task in a milestone. Please add another task before deleting this one."
                              ) }
                         </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                         <Button style={{ textTransform: "none" }} onClick={() => setOpen(false)}>{numberOfTasks !== 1 ? "No" : "Close"}</Button>
                         {numberOfTasks !== 1 && (
                              <Button style={{ textTransform: "none" }} variant='contained' onClick={handleDelete}>Yes, delete</Button>
                         )}
                    </DialogActions>
               </Dialog>
          </>
     );
}; 